import { useSelector } from 'react-redux';
import { licenseEditModeSelector } from '../data/data-selectors';
import { useCallback, useMemo } from 'react';
import { strOracleApiSender } from './stroracle-api-sender';
import appSettings from '@/app-settings.json';
// @ts-expect-error This file is not typed yet
import * as dataSelectors from '@/data/data-selectors';
import { useParams } from 'react-router-dom';
import * as Sentry from '@sentry/browser';
import { AdminUpdateConfigType } from '../types';

export interface LicenseEditModeConfig {
  type: AdminUpdateConfigType;
  externalEditLinkMode: boolean;
}

export interface LicenseEditModeConfigResponse {
  editModeConfig: LicenseEditModeConfig;
  onExternalEditButtonClick: (applicationNumber: string) => Promise<void>;
}

export default function useLicenseEditMode(): LicenseEditModeConfigResponse {
  const licenseEditMode = useSelector(licenseEditModeSelector);
  const { place } = useParams<{ place: string }>();

  const registrationPortalPlaceShortName =
    useSelector(dataSelectors.registrationPortalPlaceShortName) || place;

  const handleExternalEditButtonClick = useCallback(
    async (applicationNumber: string) => {
      try {
        const {
          data: { jwt },
        } = await strOracleApiSender.post(
          `/license-applications/${applicationNumber}/registration-system-tokens`
        );

        const templateUrl =
          licenseEditMode === AdminUpdateConfigType.ExpressPortalEditor
            ? appSettings.expressCustomerPortalUrl
            : appSettings.registrationPortalUrl;

        const registrationPortalBaseUrl = templateUrl.replace(
          '{placeShortName}',
          registrationPortalPlaceShortName
        );

        const url =
          licenseEditMode === AdminUpdateConfigType.ExpressPortalEditor
            ? `${registrationPortalBaseUrl}/admin-update-property`
            : `${registrationPortalBaseUrl}/admin-update-certificate/find-license/email`;

        const urlWithToken = `${url}?operatorLoginToken=${jwt}`;

        window.open(urlWithToken);
      } catch (error) {
        Sentry.captureException(error);
        toaster.error('An unexpected error has occurred.');
      }
    },
    [licenseEditMode, registrationPortalPlaceShortName]
  );

  const editModeConfig: LicenseEditModeConfig = useMemo(() => {
    // Defaults to RsOnPage
    const mode = licenseEditMode || AdminUpdateConfigType.RentalscapeEditor;

    return {
      type: mode,
      externalEditLinkMode:
        mode === AdminUpdateConfigType.RegistrationSystemEditor ||
        mode === AdminUpdateConfigType.ExpressPortalEditor,
    };
  }, [licenseEditMode]);

  return {
    editModeConfig,
    onExternalEditButtonClick: handleExternalEditButtonClick,
  };
}
